import React, { useEffect } from 'react';
import { Form, FormGroup, Input, Label } from 'reactstrap';
import PropTypes from 'prop-types';
import Body from '../Components/layout/Body';
import { UserTextOrNone } from '../Components/layout/DisplayUserText';
import { CHECKLIST_BUTTONS } from './ConstChecklistBtns';
import { ValuesList } from '../Components/layout/ReviewRow';

const REVIEW_CHECKLIST_HELP = `
  <div>
    <p>If you’ve gone through the Gather Info section, then you’ve entered a lot of info about the client’s goals and needs.  
    Here you can think about whether each Pick you’ve chosen really does meet those identified goals and needs.   
    </p>
    <p>This is a chance for you and the client to talk these over and decide together (along with any other team members). 
    We’ve tried to keep things simple with a checkbox (rather than rating the extent to which the need is met), 
    although we realize that in some cases it can be hard to know for sure. 
    </p>
    <p>You can always add comments that give the reasoning behind your responses here, 
    or add any other context to the decision-making that seems important. 
    </p>
    <p>
    This information will be included in the report at the end of this section. 
    </p>
    <p><strong>Note: </strong>You'll only see this step if you have chosen any Picks so far. Also, if you see 'None entered' anywhere, it simply means that you didn't enter information for that item, so we have nothing to display.</p>
  </div>
`;

const propTypes = {
  firstName: PropTypes.string,
  pronouns: PropTypes.arrayOf(PropTypes.string),
  priority: PropTypes.string,
  platforms: PropTypes.arrayOf(PropTypes.string),
  environments: PropTypes.arrayOf(PropTypes.string),
  bodyPositions: PropTypes.arrayOf(PropTypes.string),
  picks: PropTypes.arrayOf(PropTypes.object).isRequired,
  onChangeNeedsMet: PropTypes.func.isRequired,
  onChangeComments: PropTypes.func.isRequired,
  saveFormData: PropTypes.func.isRequired,
  onContinue: PropTypes.func.isRequired
};

const defaultProps = {
  firstName: 'the client',
  pronouns: ['they', 'them', 'their'],
  priority: '',
  platforms: [],
  environments: [],
  bodyPositions: [],
};

//content sub-component with unique content for this page
function ReviewTabChooseChecklistContent(props) {
  const{
    firstName,
    pronouns,
    priority,
    platforms,
    environments,
    bodyPositions,
    picks,
    onChangeNeedsMet,
    onChangeComments
  } = props;

  const activeBtns = CHECKLIST_BUTTONS;

  //*** For debugging only */
  // const picks=[{
  //   needsMet: [],
  //   accessMethodName: 'Large key keyboard'
  // }];

  const previousEntries = {
    // 'priority': priority,
    'platforms': platforms,
    'environments': environments,
    'bodyPositions': bodyPositions
  }

  if (picks.length > 0){
  return(
    <>
    {picks.map((pick, pIndex) =>
    <div key={pIndex} className="row aa-review-row border-top">
      <p><strong>Pick: </strong><UserTextOrNone userText={pick.setupName ? pick.setupName : pick.accessMethodName}/></p>
      <Form>
        <FormGroup tag="fieldset">
          <legend>For each need, check the box if this access method meets the need:</legend>
          {activeBtns.map((btn, index) =>
            <FormGroup check key={index}>
              <Input
                id={createBoxId(pIndex, index)}
                value={btn.value}
                checked={pick.needsMet.indexOf(btn.value) > -1}
                onChange={onChangeNeedsMet} 
                name="needsMet"
                type="checkbox"
              />
              {' '}
              <Label for={createBoxId(pIndex, index)} check>
                {btn.label}
                {previousEntries[btn.value]
                  &&
                  <>
                    {': '}
                    <ValuesList
                      values={previousEntries[btn.value]}
                      lookup={btn.lookup}
                      asString={true}
                    />
                  </>
                }
                {btn.value === 'priority'
                  &&
                  <>
                    {': '}
                    <ValuesList
                      values={pick.priorityActivity}
                      lookup={btn.lookup}
                      asString={true}
                    />
                  </>
                }
                <span className="aa-more-info">{btn.moreInfo}</span>
              </Label>
            </FormGroup>
          )}
        </FormGroup>

        <FormGroup>
          <Label for={createFieldId(pIndex, 0)} className="aa-bold-text">
            Your comments:
          </Label>
          <Input
            id={createFieldId(pIndex, 0)}
            value={pick.comments}
            onChange={onChangeComments}
            name="comments"
            type="textarea"
          />
        </FormGroup>
      </Form>
    </div>
    )}
    </>
  );
  }
  //TODO resolve what we're going to do when there are no Picks
  else{
    return(
    <>
    <p><strong>Note:</strong> There are no Picks shown here because you haven't chosen any.</p>
    </>
    );
  }
}

function createBoxId(pIndex, bIndex){
  return(
    "pick"+pIndex+"-box"+bIndex
  );
}
function createFieldId(pIndex, bIndex){
  return(
    "pick"+pIndex+"-field"+bIndex
  );
}

//main component for this page
//basic pattern is to set up the pageData, define the unique content for the page (above),
//then render the page using <Body> component.
//since the main goal step includes a form, receive the initial form values via props,
//along with setters to change those values based on the form input.
function ReviewTabChooseChecklist(props) {
  const{
    firstName,
    pronouns,
    priority,
    platforms,
    environments,
    bodyPositions,
    picks,
    onChangeNeedsMet,
    onChangeComments,
    onContinue,
    saveFormData,
  } = props;

  const pageData = {
    image: "./assets/images/target-icon.jpg", //not yet used
    faIcon: "fa-thumbs-o-up",
    pageSymbolClassName: "aa-section-symbol review",
    title: `Evaluate each of Your Picks in detail`,
    subTitle: `Let's double-check how well each access method in Your Picks meets the needs that you and ${firstName} identified earlier.`,
    learnMore: {linkText: "Learn More about evaluating Your Picks and what to consider",
                title: "Evaluating Your Picks",
                content: REVIEW_CHECKLIST_HELP
                },
    nextRoute: "", //not used since we have an onContinue callback
    sidebar: false
  }

  useEffect(() => {
    console.log('RTCChecklist mounted');
    return () => {
      console.log('RTCChecklist unmounting and saving...');
      saveFormData();
    }
  }, [saveFormData])

  return(
    <>
    <Body page={pageData} 
      contentComponent={
        <ReviewTabChooseChecklistContent 
          firstName={firstName} 
          pronouns={pronouns} 
          priority={priority}
          platforms={platforms}
          environments={environments}
          bodyPositions={bodyPositions}
          picks={picks}
          onChangeNeedsMet={onChangeNeedsMet}
          onChangeComments={onChangeComments}
        />
      }
      onContinue={onContinue} 
    />
    </>
  );
}

ReviewTabChooseChecklist.propTypes = propTypes;
ReviewTabChooseChecklist.defaultProps = defaultProps;

export default ReviewTabChooseChecklist;